import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slide,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CalculateIcon from '@mui/icons-material/Calculate';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DOCUMENTACION } from '../data/documentacion';

// Transición del diálogo
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Mapeo de iconos
const ICONOS = {
  Dashboard: <DashboardIcon />,
  LocalHospital: <LocalHospitalIcon />,
  CalendarMonth: <CalendarMonthIcon />,
  AccountTree: <AccountTreeIcon />,
  Calculate: <CalculateIcon />,
  Analytics: <AnalyticsIcon />,
  WhatsApp: <WhatsAppIcon />
};

// Componente para renderizar contenido con formato básico
const ContenidoFormateado = ({ texto }) => {
  if (!texto) return null;

  const lineas = texto.split('\n');
  const elementos = [];
  let enLista = false;
  let itemsLista = [];

  const cerrarLista = () => {
    if (itemsLista.length > 0) {
      elementos.push(
        <Box key={`lista-${elementos.length}`} component="ul" sx={{ pl: 2, my: 1 }}>
          {itemsLista.map((item, idx) => (
            <Typography key={idx} component="li" sx={{ fontSize: 13, color: '#475569', mb: 0.5 }}>
              {item}
            </Typography>
          ))}
        </Box>
      );
      itemsLista = [];
    }
    enLista = false;
  };

  lineas.forEach((linea, idx) => {
    const lineaTrim = linea.trim();

    // Línea vacía
    if (!lineaTrim) {
      cerrarLista();
      return;
    }

    // Título en negrita **texto**
    if (lineaTrim.startsWith('**') && lineaTrim.endsWith('**')) {
      cerrarLista();
      elementos.push(
        <Typography key={idx} sx={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', mt: 1.5, mb: 0.5 }}>
          {lineaTrim.replace(/\*\*/g, '')}
        </Typography>
      );
      return;
    }

    // Título con dos puntos **Texto:**
    if (lineaTrim.match(/^\*\*[^*]+:\*\*$/)) {
      cerrarLista();
      elementos.push(
        <Typography key={idx} sx={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', mt: 1.5, mb: 0.5 }}>
          {lineaTrim.replace(/\*\*/g, '')}
        </Typography>
      );
      return;
    }

    // Item de lista con bullet
    if (lineaTrim.startsWith('•') || lineaTrim.startsWith('-')) {
      enLista = true;
      let contenido = lineaTrim.substring(1).trim();
      // Procesar negritas dentro del item
      contenido = contenido.replace(/\*\*([^*]+)\*\*/g, '$1');
      itemsLista.push(contenido);
      return;
    }

    // Item numerado
    if (lineaTrim.match(/^\d+\./)) {
      enLista = true;
      let contenido = lineaTrim.replace(/^\d+\.\s*/, '');
      contenido = contenido.replace(/\*\*([^*]+)\*\*/g, '$1');
      itemsLista.push(contenido);
      return;
    }

    // Texto normal
    cerrarLista();
    let contenido = lineaTrim.replace(/\*\*([^*]+)\*\*/g, '$1');
    elementos.push(
      <Typography key={idx} sx={{ fontSize: 13, color: '#475569', mb: 1, lineHeight: 1.6 }}>
        {contenido}
      </Typography>
    );
  });

  cerrarLista();
  return <>{elementos}</>;
};

const CentroAyuda = ({ open, onClose }) => {
  const [busqueda, setBusqueda] = useState('');
  const [moduloSeleccionado, setModuloSeleccionado] = useState(null);
  const [seccionExpandida, setSeccionExpandida] = useState(null);
  const [vistaActual, setVistaActual] = useState('modulos'); // 'modulos', 'faq', 'atajos'

  const handleClose = () => {
    setBusqueda('');
    setModuloSeleccionado(null);
    setSeccionExpandida(null);
    setVistaActual('modulos');
    onClose();
  };

  const handleVolverAModulos = () => {
    setModuloSeleccionado(null);
    setSeccionExpandida(null);
  };

  // Filtrar módulos por búsqueda
  const modulosFiltrados = DOCUMENTACION.modulos.filter(modulo => {
    if (!busqueda) return true;
    const terminoBusqueda = busqueda.toLowerCase();
    return (
      modulo.nombre.toLowerCase().includes(terminoBusqueda) ||
      modulo.descripcion.toLowerCase().includes(terminoBusqueda) ||
      modulo.secciones.some(s =>
        s.titulo.toLowerCase().includes(terminoBusqueda) ||
        s.contenido.toLowerCase().includes(terminoBusqueda)
      )
    );
  });

  // Filtrar FAQ por búsqueda
  const faqFiltradas = DOCUMENTACION.faq.filter(item => {
    if (!busqueda) return true;
    const terminoBusqueda = busqueda.toLowerCase();
    return (
      item.pregunta.toLowerCase().includes(terminoBusqueda) ||
      item.respuesta.toLowerCase().includes(terminoBusqueda)
    );
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "#f8fafc",
          boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
          minHeight: '70vh',
          maxHeight: '85vh'
        }
      }}
    >
      {/* Header con gradiente */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 2,
          px: 3
        }}
      >
        <MenuBookIcon sx={{ color: "#fbbf24", fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>
            Centro de Ayuda
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
            Manual de usuario de NARA
          </Typography>
        </Box>
        <Chip
          label={`v${DOCUMENTACION.general.version}`}
          size="small"
          sx={{
            background: "rgba(255,255,255,0.2)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 11
          }}
        />
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Barra de navegación y búsqueda */}
        <Box sx={{
          p: 2,
          background: '#fff',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Chips de navegación */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              icon={<MenuBookIcon sx={{ fontSize: 16 }} />}
              label="Módulos"
              onClick={() => { setVistaActual('modulos'); setModuloSeleccionado(null); }}
              sx={{
                fontWeight: 600,
                fontSize: 12,
                bgcolor: vistaActual === 'modulos' ? '#1e3a5f' : '#e2e8f0',
                color: vistaActual === 'modulos' ? '#fff' : '#475569',
                '&:hover': { bgcolor: vistaActual === 'modulos' ? '#2d5a87' : '#cbd5e1' }
              }}
            />
            <Chip
              icon={<QuestionAnswerIcon sx={{ fontSize: 16 }} />}
              label="FAQ"
              onClick={() => { setVistaActual('faq'); setModuloSeleccionado(null); }}
              sx={{
                fontWeight: 600,
                fontSize: 12,
                bgcolor: vistaActual === 'faq' ? '#1e3a5f' : '#e2e8f0',
                color: vistaActual === 'faq' ? '#fff' : '#475569',
                '&:hover': { bgcolor: vistaActual === 'faq' ? '#2d5a87' : '#cbd5e1' }
              }}
            />
            <Chip
              icon={<KeyboardIcon sx={{ fontSize: 16 }} />}
              label="Atajos"
              onClick={() => { setVistaActual('atajos'); setModuloSeleccionado(null); }}
              sx={{
                fontWeight: 600,
                fontSize: 12,
                bgcolor: vistaActual === 'atajos' ? '#1e3a5f' : '#e2e8f0',
                color: vistaActual === 'atajos' ? '#fff' : '#475569',
                '&:hover': { bgcolor: vistaActual === 'atajos' ? '#2d5a87' : '#cbd5e1' }
              }}
            />
          </Box>

          {/* Barra de búsqueda */}
          <TextField
            size="small"
            placeholder="Buscar en la documentación..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, fontSize: 13, bgcolor: '#f8fafc' }
            }}
          />
        </Box>

        {/* Contenido principal */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* Vista de Módulos */}
          {vistaActual === 'modulos' && !moduloSeleccionado && (
            <Box>
              {/* Mensaje de bienvenida */}
              <Box sx={{
                p: 2,
                mb: 2,
                bgcolor: '#eff6ff',
                borderRadius: 2,
                border: '1px solid #bfdbfe'
              }}>
                <Typography sx={{ fontSize: 14, color: '#1e40af', fontWeight: 600, mb: 0.5 }}>
                  {DOCUMENTACION.general.titulo}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#3b82f6' }}>
                  {DOCUMENTACION.general.descripcion}
                </Typography>
              </Box>

              {/* Lista de módulos */}
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
                Selecciona un módulo para ver su documentación
              </Typography>
              <List sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                {modulosFiltrados.map((modulo, idx) => (
                  <React.Fragment key={modulo.id}>
                    {idx > 0 && <Divider />}
                    <ListItemButton
                      onClick={() => setModuloSeleccionado(modulo)}
                      sx={{
                        py: 1.5,
                        '&:hover': { bgcolor: '#f8fafc' }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: modulo.color + '15',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: modulo.color
                        }}>
                          {ICONOS[modulo.icono] || <HelpOutlineIcon />}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                            {modulo.nombre}
                          </Typography>
                        }
                        secondary={
                          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                            {modulo.descripcion}
                          </Typography>
                        }
                      />
                      <Chip
                        label={`${modulo.secciones.length} secciones`}
                        size="small"
                        sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontSize: 10, fontWeight: 600 }}
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>

              {modulosFiltrados.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
                    No se encontraron resultados para "{busqueda}"
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Vista de Módulo Seleccionado */}
          {vistaActual === 'modulos' && moduloSeleccionado && (
            <Box>
              {/* Botón volver */}
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleVolverAModulos}
                sx={{
                  mb: 2,
                  color: '#64748b',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13
                }}
              >
                Volver a módulos
              </Button>

              {/* Header del módulo */}
              <Box sx={{
                p: 2,
                mb: 2,
                bgcolor: moduloSeleccionado.color + '10',
                borderRadius: 2,
                border: `1px solid ${moduloSeleccionado.color}30`,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: moduloSeleccionado.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff'
                }}>
                  {ICONOS[moduloSeleccionado.icono] || <HelpOutlineIcon />}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1e293b' }}>
                    {moduloSeleccionado.nombre}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                    {moduloSeleccionado.descripcion}
                  </Typography>
                </Box>
              </Box>

              {/* Secciones del módulo */}
              {moduloSeleccionado.secciones.map((seccion, idx) => (
                <Accordion
                  key={idx}
                  expanded={seccionExpandida === idx}
                  onChange={() => setSeccionExpandida(seccionExpandida === idx ? null : idx)}
                  sx={{
                    mb: 1,
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    boxShadow: 'none'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
                    sx={{
                      '&:hover': { bgcolor: '#f8fafc' },
                      borderRadius: 2
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                      {seccion.titulo}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 3, pb: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <ContenidoFormateado texto={seccion.contenido} />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* Vista de FAQ */}
          {vistaActual === 'faq' && (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 2, textTransform: 'uppercase' }}>
                Preguntas Frecuentes
              </Typography>
              {faqFiltradas.map((item, idx) => (
                <Accordion
                  key={idx}
                  sx={{
                    mb: 1,
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    boxShadow: 'none'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
                    sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <QuestionAnswerIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                        {item.pregunta}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 3, pb: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                      {item.respuesta}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}

              {faqFiltradas.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
                    No se encontraron preguntas para "{busqueda}"
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Vista de Atajos */}
          {vistaActual === 'atajos' && (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 2, textTransform: 'uppercase' }}>
                Atajos de Teclado
              </Typography>
              <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                {DOCUMENTACION.atajos.map((atajo, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: idx < DOCUMENTACION.atajos.length - 1 ? '1px solid #e2e8f0' : 'none',
                      '&:hover': { bgcolor: '#f8fafc' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: '#1e293b',
                        color: '#fff',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {atajo.tecla}
                      </Box>
                      <Typography sx={{ fontSize: 13, color: '#1e293b' }}>
                        {atajo.accion}
                      </Typography>
                    </Box>
                    <Chip
                      label={atajo.modulo}
                      size="small"
                      sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontSize: 10, fontWeight: 600 }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid #e2e8f0", background: "#fff", px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            textTransform: "none",
            color: "#64748b",
            borderColor: "#e2e8f0"
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CentroAyuda;
